import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Printer } from 'lucide-react';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { examinationsApi, resultsApi } from '@/services/api/endpoints/examinations';
import { queryKeys } from '@/services/api/queryKeys';
import { ROUTES } from '@/app/router/routes';
import type { StudentResult, SubjectResult } from '@/types/examination';
import { isApiError } from '@/utils/ApiError';
import { formatDateTime, humanise, orDash } from '@/utils/format';

/** One stored result by its enrolment; "not found" means no result yet, not an error. */
async function fetchResult(examinationId: string, enrollmentId: string): Promise<StudentResult | null> {
  try {
    return await resultsApi.forStudent(examinationId, enrollmentId);
  } catch (error) {
    if (isApiError(error) && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * A printable report card for one student and one examination.
 *
 * Rendered entirely from the server's stored result: every mark, percentage,
 * grade, total and position below is a value it returned. A report card issued
 * in March has to say in July what it said then, which is only true if the
 * browser never recomputes any of it.
 *
 * The result endpoint is keyed by enrolment, which every result carries. The
 * results table links here with it in `?enrollment=`; a bare link (a bookmark,
 * a pasted URL) finds it on the examination's result list by student instead.
 *
 * Printing uses the shared print stylesheet — only the `.report-card` article
 * survives — so "save as PDF" produces the card without the application around it.
 */
export function ReportCardPage() {
  const { id: examinationId = '', studentId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const linkedEnrollmentId = searchParams.get('enrollment');

  const examination = useQuery({
    queryKey: queryKeys.examinations.detail(examinationId),
    queryFn: () => examinationsApi.get(examinationId),
    enabled: examinationId !== '',
  });

  const roster = useQuery({
    queryKey: queryKeys.examinations.results(examinationId),
    queryFn: () => resultsApi.list(examinationId),
    enabled: examinationId !== '' && linkedEnrollmentId === null,
  });

  const enrollmentId =
    linkedEnrollmentId ?? roster.data?.find((row) => row.studentId === studentId)?.enrollmentId;

  const result = useQuery({
    queryKey: [...queryKeys.examinations.results(examinationId), 'enrollment', enrollmentId] as const,
    queryFn: () => fetchResult(examinationId, enrollmentId as string),
    enabled: examinationId !== '' && enrollmentId !== undefined,
  });

  const back = (
    <Link
      to={ROUTES.examinationResults(examinationId)}
      className="mb-3 inline-flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-ink print:hidden"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
      {examination.data?.name ?? 'Results'}
    </Link>
  );

  const failed = examination.error ?? roster.error ?? result.error;
  if (failed !== null && failed !== undefined) {
    return (
      <div>
        {back}
        <ErrorState
          error={failed}
          onRetry={() => {
            void examination.refetch();
            if (linkedEnrollmentId === null) {
              void roster.refetch();
            }
            if (enrollmentId !== undefined) {
              void result.refetch();
            }
          }}
        />
      </div>
    );
  }

  const resolving = linkedEnrollmentId === null && roster.isPending;
  if (examination.isPending || resolving || (enrollmentId !== undefined && result.isPending)) {
    return <LoadingState label="Loading the report card" />;
  }

  const card = enrollmentId === undefined ? null : result.data;
  if (card === null || card === undefined) {
    return (
      <div>
        {back}
        <Card>
          <EmptyState
            title="No result for this student"
            description="No result has been calculated for this student in this examination yet."
          />
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        {back}
        <Button size="sm" onClick={() => window.print()} className="mb-3">
          <Printer className="h-3.5 w-3.5" aria-hidden="true" />
          Print
        </Button>
      </div>

      {card.publishedAt === undefined || card.publishedAt === null ? (
        <p className="mb-4 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning print:hidden">
          This result has not been published. Anything printed now is provisional.
        </p>
      ) : null}

      <ReportCard card={card} />
    </div>
  );
}

/** The printable card itself; shared with the student and parent portal. */
export function ReportCard({ card }: { card: StudentResult }) {
  const showGradePoints = card.subjects.some(
    (subject) => subject.gradePoint !== undefined && subject.gradePoint !== null,
  );
  const hasPosition = card.position !== undefined && card.position !== null;

  const facts: { label: string; value: string }[] = [
    { label: 'Student', value: orDash(card.studentName) },
    { label: 'Student number', value: orDash(card.studentNumber) },
    { label: 'Roll number', value: orDash(card.rollNumber) },
    { label: 'Class', value: [card.gradeName, card.sectionName].filter(Boolean).join(' · ') || '—' },
    { label: 'Academic year', value: orDash(card.academicYearName) },
    { label: 'Term', value: orDash(card.termName) },
  ];

  const summary: { label: string; value: string }[] = [
    { label: 'Total', value: `${card.totalObtained} / ${card.totalMaxMarks}` },
    { label: 'Percentage', value: `${card.percentage}%` },
    { label: 'Grade', value: orDash(card.grade) },
    ...(card.gpa !== undefined && card.gpa !== null ? [{ label: 'GPA', value: String(card.gpa) }] : []),
    ...(hasPosition
      ? [
          {
            label: 'Position',
            value:
              card.rankingScope === undefined || card.rankingScope === null
                ? String(card.position)
                : `${card.position} (${humanise(card.rankingScope).toLowerCase()})`,
          },
        ]
      : []),
    { label: 'Papers passed', value: `${card.subjectsPassed} of ${card.subjectsTotal}` },
  ];

  return (
    <article className="report-card rounded-xl bg-surface p-4 shadow-xs ring-1 ring-line sm:p-8 print:rounded-none print:p-0 print:shadow-none print:ring-0">
      <header className="border-b-2 border-line pb-4">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-subtle">Report card</p>
        <h1 className="mt-1 text-2xl leading-tight text-ink">{card.examinationName}</h1>
      </header>

      <dl className="grid gap-x-6 gap-y-3 border-b border-line py-4 sm:grid-cols-3 print:grid-cols-3">
        {facts.map((fact) => (
          <div key={fact.label}>
            <dt className="text-[11px] font-semibold uppercase tracking-wider text-subtle">{fact.label}</dt>
            <dd className="mt-0.5 text-sm text-ink">{fact.value}</dd>
          </div>
        ))}
      </dl>

      {card.subjects.length === 0 ? (
        <p className="py-6 text-sm text-muted">No papers are recorded on this result.</p>
      ) : (
        <div className="-mx-4 overflow-x-auto sm:mx-0 print:mx-0 print:overflow-visible">
          <table className="w-full min-w-[640px] text-sm print:min-w-0">
            <thead>
              <tr className="border-b border-line">
                {['Subject', 'Max', 'Pass', 'Obtained', '%', 'Grade', ...(showGradePoints ? ['GP'] : []), 'Result', 'Remarks'].map(
                  (header) => (
                    <th
                      key={header}
                      scope="col"
                      className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-subtle"
                    >
                      {header}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {card.subjects.map((subject) => (
                <SubjectRow key={subject.examinationSubjectId} subject={subject} showGradePoints={showGradePoints} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-3 border-t-2 border-line pt-4 sm:grid-cols-3 lg:grid-cols-6 print:grid-cols-6">
        {summary.map((item) => (
          <div key={item.label}>
            <dt className="text-[11px] font-semibold uppercase tracking-wider text-subtle">{item.label}</dt>
            <dd className="mt-0.5 text-base font-medium tabular-nums text-ink">{item.value}</dd>
          </div>
        ))}
      </dl>

      <p className="mt-4 text-sm">
        <span className="text-muted">Overall result: </span>
        <span className="font-semibold text-ink">{card.passing ? 'Pass' : 'Fail'}</span>
      </p>

      {(card.teacherRemarks || card.headRemarks) && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 print:grid-cols-2">
          {card.teacherRemarks && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-subtle">Class teacher’s remarks</p>
              <p className="mt-1 whitespace-pre-line text-sm text-ink">{card.teacherRemarks}</p>
            </div>
          )}
          {card.headRemarks && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-subtle">Head’s remarks</p>
              <p className="mt-1 whitespace-pre-line text-sm text-ink">{card.headRemarks}</p>
            </div>
          )}
        </div>
      )}

      <footer className="mt-8 border-t border-line pt-3 text-[11px] text-subtle">
        {card.publishedAt === undefined || card.publishedAt === null
          ? 'Provisional — not yet published.'
          : `Published ${formatDateTime(card.publishedAt)}.`}
      </footer>
    </article>
  );
}

/** One paper. Absent and exempt are said in words — neither is a zero. */
function SubjectRow({ subject, showGradePoints }: { subject: SubjectResult; showGradePoints: boolean }) {
  const graded = subject.markStatus === 'GRADED';
  return (
    <tr>
      <td className="px-3 py-2.5 font-medium text-ink">{orDash(subject.subjectName)}</td>
      <td className="px-3 py-2.5 tabular-nums text-muted">{subject.maxMarks}</td>
      <td className="px-3 py-2.5 tabular-nums text-muted">{subject.passingMarks}</td>
      <td className="px-3 py-2.5 tabular-nums text-ink">
        {graded ? orDash(subject.obtainedMarks) : humanise(subject.markStatus)}
      </td>
      <td className="px-3 py-2.5 tabular-nums text-muted">
        {subject.percentage === undefined || subject.percentage === null ? '—' : `${subject.percentage}%`}
      </td>
      <td className="px-3 py-2.5 font-medium text-ink">{orDash(subject.grade)}</td>
      {showGradePoints && <td className="px-3 py-2.5 tabular-nums text-muted">{orDash(subject.gradePoint)}</td>}
      <td className="px-3 py-2.5">
        {subject.passing === undefined || subject.passing === null ? (
          <span className="text-subtle">—</span>
        ) : (
          <Badge tone={subject.passing ? 'success' : 'danger'}>{subject.passing ? 'Pass' : 'Fail'}</Badge>
        )}
      </td>
      <td className="px-3 py-2.5 text-xs text-muted">{subject.remarks ?? ''}</td>
    </tr>
  );
}
